import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useStores } from "../../../use-stores";
import { useLocation, useNavigate } from "react-router-dom";
import { Badge, Box, Collapse, Grid2, IconButton, Paper } from "@mui/material";
import { observer } from "mobx-react";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { SortType } from "../../../../../models/SortType";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { InventoryFilters } from "../../../../../models/inventory/InventoryListingFilters";
import InventoryListingTable from "../../components/Inventory/InventoryListingTable";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import {
  FilterMenuType,
  FilterSidebarMenu,
} from "../../../../../models/FilterMenu";
import { ModuleType } from "../../../../../models/ModuleType";
import FilterSidebar from "../../../views/components/filter/FilterSidebar";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { useTheme } from "@mui/material/styles";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

const resolveSearchText = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  // Inventory IDs can be a single digit; customer names need at least 3 chars
  const isNumeric = /^\d+$/.test(trimmed);
  if (isNumeric) return trimmed;
  if (trimmed.length >= 3) return trimmed;
  return undefined;
};

const InventoryListing: React.FC = () => {
  const theme = useTheme();

  const { inventoryStore, userStore } = useStores();
  // const [isLoading, setIsLoading] = useState(false);
  const { showError } = useNotification();
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [filterCount, setFilterCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search_text") || "",
  );
  const filterFields = [
    "order_id",
    "inventory_id",
    "inventory_type",
    "customer_id",
  ];

  const getArrayFromUrlParam = (param: string | null): FilterValue[] => {
    if (!param) return [];
    return param.split(",").map((item) => {
      return { id: item, value: item };
    });
  };
  const initialFilters: InventoryFilters = {
    page_no: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string) || 1
      : 1,
    search_text: searchParams.get("search_text") || undefined,
    sort_by: searchParams.get("sort_by") || "created_date",
    sort_type:
      searchParams.get("sort_type") === "ASC" ? SortType.ASC : SortType.DESC,
    customer_id: getArrayFromUrlParam(searchParams.get("customer_id")),
    customer_name: getArrayFromUrlParam(searchParams.get("customer_name")).map(
      (item) => item.value,
    ),
    order_id: getArrayFromUrlParam(searchParams.get("order_id")),
    inventory_id: getArrayFromUrlParam(searchParams.get("inventory_id")),
    inventory_type: searchParams.get("inventory_type")
      ? parseInt(searchParams.get("inventory_type") as string)
      : undefined,
  };
  const initialParams: InventoryFilters = {
    ...initialFilters,
    limit: parseInt(searchParams.get("limit") || "30"),
  };
  const [filters, setFilters] = useState<InventoryFilters>(initialFilters);
  const [params, setParams] = useState<InventoryFilters>(initialParams);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  useEffect(() => {
    const newSearch = resolveSearchText(debouncedSearchTerm);
    setParams((prev) => {
      const prevSearch = prev.search_text ?? "";
      if (prevSearch === (newSearch ?? "")) return prev;
      return { ...prev, search_text: newSearch, page_no: 1 };
    });
  }, [debouncedSearchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      if (
        !userStore.hasPermission([
          EmployeeRoles.VIEW_INVENTORY,
          EmployeeRoles.CREATE_INVENTORY,
        ])
      )
        return;

      try {
        await inventoryStore.getListing(params);
      } catch (error) {
        showError("Error", "Failed to fetch inventory data");
      }
    };

    fetchData();
  }, [params, inventoryStore, showError, userStore]);

  const handleSearch = (query: string) => {
    setSearchTerm(query);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      const resolvedSearch = resolveSearchText(searchTerm);
      if (resolvedSearch) {
        setParams((prev) => ({
          ...prev,
          search_text: resolvedSearch,
          page_no: 1,
          sortBy: filters.sort_by || "created_date",
          sort_type: filters.sort_type || SortType.ASC,
        }));
      } else {
        setParams((prev) => ({
          ...prev,
          search_text: undefined,
          page_no: 1,
        }));
      }
    }
  };
  const handleFilterToggle = () => {
    setOpenFilter(!openFilter);
  };

  const handleRefresh = async () => {
    try {
      await inventoryStore.getListing(params);
    } catch (error) {
      showError("Error", "Failed to fetch inventory data");
    }
  };
  const handleFilterChange = useCallback(
    (
      key: keyof InventoryFilters,
      value: InventoryFilters[keyof InventoryFilters],
    ) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  const storeCustomerName = (customerId: string, customerName: string) => {
    const customerNames = JSON.parse(
      localStorage.getItem("customerNames") || "{}",
    );
    customerNames[customerId] = customerName;
    localStorage.setItem("customerNames", JSON.stringify(customerNames));
  };
  const getCustomerName = useCallback((customerId: string): string => {
    const customerNames = JSON.parse(
      localStorage.getItem("customerNames") || "{}",
    );
    return customerNames[customerId] || customerId;
  }, []);

  const filterMenu: FilterSidebarMenu[] = useMemo(() => {
    return [
      {
        moduleType: ModuleType.CUSTOMER,
        fieldType: FilterMenuType.MULTI_SELECT,
        subType: "search_customer",
        label: "Customer",
        placeholder: "Enter Customer name",
        value: filters.customer_id.map((item) => ({
          id: typeof item === "object" && "id" in item ? item.id : item,
          value:
            typeof item === "object" && "id" in item
              ? getCustomerName(item.id)
              : getCustomerName(item.toString()),
        })),
        onChange: (value: FilterValue[]) => {
          // Store names when selected
          value.forEach((item) => {
            if (item.value !== item.id) {
              storeCustomerName(item.id, item.value);
            }
          });
          handleFilterChange("customer_id", value);
        },
      },
      {
        moduleType: ModuleType.ORDER,
        fieldType: FilterMenuType.MULTI_SELECT,
        subType: "search_order",
        label: "Order",
        placeholder: "Search Order",
        value: filters.order_id,
        onChange: (value: FilterValue[]) =>
          handleFilterChange("order_id", value),
      },
      {
        moduleType: ModuleType.INVENTORY,
        fieldType: FilterMenuType.MULTI_SELECT,
        label: "Inventory Id",
        placeholder: "Search Inventory Id",
        value: filters.inventory_id,
        onChange: (value: FilterValue[]) =>
          setFilters((prev) => ({ ...prev, inventory_id: value })),
      },
      {
        moduleType: ModuleType.ORDER,
        fieldType: FilterMenuType.LOCAL_SINGLE_SELECT,
        label: "Inventory Type",
        list: [
          { id: "1", value: "Order Level Inventory" },
          { id: "2", value: "Customer Level Inventory" },
        ],
        value:
          filters.inventory_type !== undefined
            ? [
                {
                  id: filters.inventory_type.toString(),
                  value:
                    filters.inventory_type === 1
                      ? "Order Level Inventory"
                      : "Customer Level Inventory",
                },
              ]
            : [],
        onChange: (selectedValues: FilterValue[]) => {
          if (selectedValues.length > 0) {
            handleFilterChange(
              "inventory_type",
              parseInt(selectedValues[0].id),
            );
          } else {
            handleFilterChange("inventory_type", undefined);
          }
        },
      },
    ];
  }, [filters, handleFilterChange, getCustomerName]);

  const handleApplyFilters = () => {
    const newParams: InventoryFilters = {
      ...params,
      order_id: filters.order_id.map((item) =>
        typeof item === "object" ? Number(item.id) : item,
      ),
      customer_id: filters.customer_id.map((item) =>
        typeof item === "object" ? Number(item.id) : item,
      ),
      inventory_id: filters.inventory_id.map((item) =>
        typeof item === "object" ? Number(item.id) : item,
      ),
      sort_by: filters.sort_by,
      sort_type: filters.sort_type,
      page_no: params.page_no,
      limit: params.limit,
      inventory_type: filters.inventory_type,
    };

    setParams(newParams);
    handleFilterToggle();
    handleFilterCount();
  };

  const handleClearFilters = () => {
    const clearedFilters: InventoryFilters = {
      page_no: 1,
      search_text: undefined,
      sort_by: "created_date",
      sort_type: SortType.DESC,
      customer_id: [],
      customer_name: [],
      order_id: [],
      inventory_id: [],
      inventory_type: undefined,
    };

    const clearedParams: InventoryFilters = {
      ...clearedFilters,
      limit: params.limit,
    };

    setFilters(clearedFilters);
    setParams(clearedParams);

    setSearchTerm("");

    handleFilterToggle();

    navigate(
      {
        search: `?page=1&limit=${params.limit}&sort_by=created_date&sort_type=DESC`,
      },
      { replace: true },
    );
  };
  useEffect(() => {
    const count = filterFields.reduce((count, field) => {
      const value = filters[field as keyof InventoryFilters];

      if (Array.isArray(value) && value.length > 0) {
        return count + 1;
      } else if (field === "inventory_type" && value !== undefined) {
        return count + 1;
      }

      return count;
    }, 0);
    setFilterCount(count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const updateQueryParams = () => {
      const newSearchParams = new URLSearchParams();
      if (params.page_no)
        newSearchParams.set("page", params.page_no.toString());
      if (params.limit) newSearchParams.set("limit", params.limit.toString());
      if (params.sort_by) newSearchParams.set("sort_by", params.sort_by);
      if (params.sort_type) newSearchParams.set("sort_type", params.sort_type);
      if (params.search_text)
        newSearchParams.set("search_text", params.search_text);

      const arrayFilters = ["customer_id", "order_id", "inventory_id"];
      arrayFilters.forEach((key) => {
        const value = params[key as keyof InventoryFilters];
        if (Array.isArray(value) && value.length > 0) {
          const paramValue = value
            .map((item) => (typeof item === "object" ? item.id : item))
            .join(",");
          newSearchParams.set(key, paramValue);
        }
      });
      if (params.customer_name && params.customer_name.length > 0) {
        newSearchParams.set("customer_name", params.customer_name.join(","));
      }

      if (params.inventory_type !== undefined) {
        newSearchParams.set("inventory_type", params.inventory_type.toString());
      }

      navigate({ search: newSearchParams.toString() }, { replace: true });
    };

    updateQueryParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate]);

  useEffect(() => {
    // On mount, apply filters into params if there are values
    const restoredParams: InventoryFilters = {
      ...params,
      customer_id: filters.customer_id.map((item) =>
        typeof item === "object" ? Number(item.id) : item,
      ),
      order_id: filters.order_id.map((item) =>
        typeof item === "object" ? Number(item.id) : item,
      ),
      inventory_id: filters.inventory_id.map((item) =>
        typeof item === "object" && "id" in item ? Number(item.id) : item,
      ),
      inventory_type: filters.inventory_type,
    };

    setParams(restoredParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setParams((prev) => ({ ...prev, page_no: newPage }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({ ...prev, limit: newLimit, page_no: 1 }));
  };
  const handleFilterCount = () => {
    const count = filterFields.reduce((count, field) => {
      const value = filters[field as keyof InventoryFilters];
      if (Array.isArray(value) && value.length > 0) {
        return count + 1;
      } else if (
        typeof value === "number" &&
        field === "inventory_type" &&
        value !== -1
      ) {
        return count + 1;
      }
      return count;
    }, 0);
    setFilterCount(count);
  };
  // const handleNavigateByProductId = (id: number) => {
  //   navigate(`${id}/packs`);
  // };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {!userStore.hasPermission([
        EmployeeRoles.VIEW_INVENTORY,
        EmployeeRoles.CREATE_INVENTORY,
      ]) && (
        <Paper>
          <Box
            sx={{
              marginTop: "0.6rem",
              paddingX: 4,
              paddingY: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
              borderRadius: "4px",
              minHeight: { xs: "60vh", md: window.innerHeight - 110 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
                mb: 2,
              }}
            >
              <CustomTypography variant="heading3">Inventory</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View inventory. Search by customer name.
              </CustomTypography>
            </Box>
            <PermissionBlockLabel />
          </Box>
        </Paper>
      )}

      {userStore.hasPermission([
        EmployeeRoles.VIEW_INVENTORY,
        EmployeeRoles.CREATE_INVENTORY,
      ]) && (
        <Paper>
          <Box
            sx={{
              marginTop: "0.6rem",
              paddingX: 4,
              paddingY: 2,
              boxShadow:
                "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
              borderRadius: "4px",
              minHeight: { xs: "60vh", md: window.innerHeight - 110 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                gap: 0.4,
                mb: 2,
              }}
            >
              <CustomTypography variant="heading3">Inventory</CustomTypography>
              <CustomTypography
                variant="body2"
                color={theme.palette.customColors?.greyTextColor}
              >
                View inventory. Search by customer name.
              </CustomTypography>
            </Box>

            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                borderRadius: 2,
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() => setIsSearchExpanded(!isSearchExpanded)}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 0.5,
                  cursor: "pointer",
                  borderBottom: isSearchExpanded ? "1px solid" : "none",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <CustomTypography variant="heading6">Search</CustomTypography>
                <IconButton size="small">
                  {isSearchExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={isSearchExpanded} timeout="auto" unmountOnExit>
                <Grid2 container spacing={2} columns={12} padding={2}>
                  <Grid2 size={{ xs: 12, sm: 12, md: 9, lg: 9 }}>
                    <CustomTextField
                      width={"100%"}
                      placeholder="Search by customer name or inventory id"
                      value={searchTerm}
                      onChange={(e) => {
                        handleSearch(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleKeyDown(e);
                        }
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 12, md: 3, lg: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1.5,
                        alignItems: "center",
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <AppButton
                          variant="grayscale"
                          label="Filter"
                          startIcon={<FilterAltOutlinedIcon />}
                          onClick={() => {
                            handleFilterToggle();
                          }}
                          customStyles={{ width: "115px" }}
                        />
                        {filterCount > 0 && (
                          <Badge
                            color="primary"
                            badgeContent={filterCount}
                            sx={{
                              position: "absolute",
                              top: 2,
                              right: 2,
                              transform: "translate(50%, -50%)",
                            }}
                          />
                        )}
                      </Box>
                      <AppButton
                        variant="grayscale"
                        label="Refresh"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        customStyles={{ width: "115px" }}
                      />
                    </Box>
                  </Grid2>
                </Grid2>
              </Collapse>
            </Paper>
            <Box>
              <InventoryListingTable
                maxHeight={window.innerHeight - 350}
                inventories={inventoryStore.listing}
                limit={params.limit || inventoryStore.limit}
                page_no={params.page_no}
                total_count={inventoryStore.total_count}
                total_pages={inventoryStore.total_pages}
                sortBy={params.sort_by}
                sortType={params.sort_type}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                onSortChange={(column, sortType) => {
                  setParams((prev) => ({
                    ...prev,
                    sort_by: column,
                    sort_type: sortType,
                  }));
                }}
                // handleNavigateById={(id: number) =>
                //   handleNavigateByProductId(id)
                // }
              />
            </Box>
            {openFilter && (
              <FilterSidebar
                isOpen={openFilter}
                items={filterMenu}
                onApply={handleApplyFilters}
                handleClear={handleClearFilters}
                toggleFilter={handleFilterToggle}
                disablePastDates={false}
              />
            )}
          </Box>
        </Paper>
      )}
    </Grid2>
  );
};

export default observer(InventoryListing);
